import React, { useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Stepper from "../components/Stepper";
import BiometricsCard from "../components/BiometricsCard";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import PropertyInfoCard from "../components/PropertyInfoCard";
import OverlaySpinner from "../components/OverlaySpinner";
import SummaryCard from "../components/SummaryCard";
import PaymentModal from '../components/PaymentModal';
import ConfirmGenerateModal from '../components/ConfirmGenerateModal';
import { DOCUMENT_TYPE } from '../../../shared/types/models/document';
import UserAgreementCard from "../components/UserAgreementCard";
import CountyNoticeCard from "../components/CountyNoticeCard";
import type { CountyNotice } from "../components/CountyNoticeCard";
import DocumentDetailsCard from "../components/DocumentDetailsCard";
import InstrumentDetailsCard from "../components/InstrumentDetailsCard";
import SimpleInstrumentCard from "../components/SimpleInstrumentCard";
import AdvancedGrantorCard from "../components/AdvancedGrantorCard";
import ParcelCollectionCard from "../components/ParcelCollectionCard";
import DynamicFormCard from "../components/DynamicFormCard";
import CustomFieldsCard from "../components/CustomFieldsCard";
import SimplePropertyAddressCard from "../components/SimplePropertyAddressCard";
import FlowSelectorCard from "../components/FlowSelectorCard";
import OptionsCard from "../components/OptionsCard";
import PortfolioBuilderCard from "../components/PortfolioBuilderCard";
import LastChanceReviewCard from "../components/LastChanceReviewCard";
import type { Freeze } from "../../../shared/types/models/freeze";

// ---------------------------------------------------------------------------
// Helper: Parse a county schema (string or object) into a plain object
// ---------------------------------------------------------------------------
function parseSchema(raw: any): any {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

// ---------------------------------------------------------------------------
// Helper: Resolve the flows map from a schema, handling lifecycle-specific
// flows (`releaseFlows`, `modifyFlows`), `flows`, and legacy (`uiFlows.steps`).
// ---------------------------------------------------------------------------
function resolveFlows(
  schema: any,
  countyName?: string,
  docType?: number | string,
): Record<string, { label: string; description?: string; steps: any[] }> | null {
  if (!schema) return null;

  const isRelease = docType === DOCUMENT_TYPE.LIEN_RELEASE || docType === 'LIEN_RELEASE';
  const isAmendment = docType === DOCUMENT_TYPE.AMENDMENT || docType === 'AMENDMENT';

  if (isRelease && schema.releaseFlows && typeof schema.releaseFlows === 'object' && Object.keys(schema.releaseFlows).length > 0) {
    return schema.releaseFlows;
  }
  if (isAmendment && schema.modifyFlows && typeof schema.modifyFlows === 'object' && Object.keys(schema.modifyFlows).length > 0) {
    return schema.modifyFlows;
  }

  // Standard format: schema.flows = { flowKey: { label, steps } }
  if (schema.flows && typeof schema.flows === 'object') {
    const flowEntries = Object.entries(schema.flows) as [string, any][];
    if (flowEntries.length > 0) return schema.flows;
  }

  // Legacy format: schema.uiFlows.steps at top level → wrap as single flow
  const legacySteps = schema?.uiFlows?.steps;
  if (Array.isArray(legacySteps) && legacySteps.length > 0) {
    return {
      default: {
        label: countyName || 'Default',
        steps: legacySteps,
      },
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// FlowPage — Unified workflow page for all flow types
// ---------------------------------------------------------------------------
const FlowPageInner: React.FC = () => {
  const params = useParams<{ documentId?: string; freezeId?: string; step?: string }>();
  const documentLookup = params.documentId;
  const step = params.step;
  const location = useLocation();
  const navigate = useNavigate();
  const { freeze, setFreeze, parcels, setParcels, document, setDocument, state, setState, county, setCounty, entities, setEntities, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();
  const { getToken } = useKeycloak();
  const [freezeError] = useState<string | null>(null);
  const [loadingFreeze] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [overlayError, setOverlayError] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [confirmModalSuccess, setConfirmModalSuccess] = useState(false);

  console.log('[FlowPage] Loaded', { path: location.pathname, documentLookup, document, freeze, state, county });

  // If document is already generated, auto-redirect to details page so they can't go backwards
  React.useEffect(() => {
    if (document?.generated && document.lookup === documentLookup) {
      navigate(`/document/${documentLookup}/details`, { replace: true });
    }
  }, [document, documentLookup, navigate]);
  // =========================================================================
  // Data fetching — load document, freeze, state, county, entities
  // =========================================================================
  React.useEffect(() => {
    const fetchAll = async () => {
      if (!documentLookup || !apiBaseUrl) return;
      if (
        document && document.lookup === documentLookup &&
        freeze && freeze.lookup === document.freezeLookup &&
        state && state.id === freeze.stateId &&
        county && county.id === freeze.countyId
      ) {
        setLoadingOverlay(false);
        return;
      }
      setLoadingOverlay(true);
      setOverlayError(null);
      try {
        const headers: Record<string, string> = {};
        if (getToken) {
          const token = await getToken();
          if (token) headers["Authorization"] = `Bearer ${token}`;
        }
        // 1. Fetch document
        let doc = document;
        if (!doc || doc.lookup !== documentLookup) {
          const docRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}`, { headers });
          if (!docRes.ok) throw new Error("Document not found");
          const docData = await docRes.json();
          if (!docData?.data) throw new Error("Document not found");
          setDocument(docData.data);
          doc = docData.data;
          setEntities([]);
        }
        // 2. Fetch freeze
        let fzd = freeze;
        if (!fzd || fzd.lookup !== doc.freezeLookup) {
          if (!doc?.freezeLookup) throw new Error("Document missing freezeLookup");
          const freezeRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${doc.freezeLookup}`, { headers });
          if (!freezeRes.ok) throw new Error("Freeze not found");
          const freezeData = await freezeRes.json();
          if (!freezeData?.data) throw new Error("Freeze not found");
          setFreeze(freezeData.data);
          if (freezeData.data.parcels) setParcels(freezeData.data.parcels);
          fzd = freezeData.data;
        }
        // 3. Fetch state
        if (!state || state.id !== fzd.stateId) {
          if (!fzd?.stateId) throw new Error("Freeze missing stateId");
          const stateRes = await fetch(`${apiBaseUrl}/api/states/${fzd.stateId}`, { headers });
          if (!stateRes.ok) throw new Error("State not found");
          const stateData = await stateRes.json();
          if (!stateData?.data) throw new Error("State not found");
          setState(stateData.data);
        }
        // 4. Fetch county (with schema)
        if (!county || county.id !== fzd.countyId || county.documentSchema === undefined) {
          if (!fzd?.countyId) throw new Error("Freeze missing countyId");
          const countyRes = await fetch(`${apiBaseUrl}/api/counties/${fzd.countyId}?all=true`, { headers, cache: 'no-store' });
          if (!countyRes.ok) throw new Error("County not found");
          const countyData = await countyRes.json();
          if (!countyData?.data) throw new Error("County not found");
          setCounty(countyData.data);
        }
        // 5. Fetch entities
        if ((entities.length === 0 || doc.lookup !== document?.lookup) && doc?.lookup) {
          try {
            const entityRes = await fetch(`${apiBaseUrl}/api/frozendeed/entities?documentLookup=${doc.lookup}`, { headers });
            if (entityRes.ok) {
              const entityData = await entityRes.json();
              if (entityData?.success && Array.isArray(entityData.data)) {
                setEntities(entityData.data);
              }
            }
          } catch (err) {
            console.error('Error fetching entities:', err);
          }
        }
      } catch (err: unknown) {
        setOverlayError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoadingOverlay(false);
      }
    };
    fetchAll();
    // eslint-disable-next-line
  }, [documentLookup, apiBaseUrl]);

  // =========================================================================
  // Schema → Flows resolution (document_schema is single source of truth)
  // =========================================================================
  const schema = React.useMemo(() => {
    const ds = parseSchema(county?.documentSchema);
    const sds = parseSchema(county?.simpleDocumentSchema);
    if (ds && (ds.flows || ds.uiFlows?.steps)) return ds;
    if (sds && (sds.flows || sds.uiFlows?.steps)) return sds;
    return ds || sds || null;
  }, [county?.documentSchema, county?.simpleDocumentSchema]);

  const isRelease = document?.type === DOCUMENT_TYPE.LIEN_RELEASE || document?.type === 'LIEN_RELEASE';
  const isAmendment = document?.type === DOCUMENT_TYPE.AMENDMENT || document?.type === 'AMENDMENT';
  const hasDedicatedReleaseFlow = Boolean(isRelease && schema?.releaseFlows && Object.keys(schema.releaseFlows).length > 0);

  const flows = React.useMemo(
    () => resolveFlows(schema, county?.name, document?.type),
    [schema, county?.name, document?.type]
  );

  // Determine the active flow — from freeze.selectedFlow, or auto-select if only one
  const [localSelectedFlow, setLocalSelectedFlow] = useState<string | null>(null);
  const activeFlowKey = freeze?.selectedFlow || localSelectedFlow;

  const activeFlow = React.useMemo(() => {
    if (!flows) return null;
    const keys = Object.keys(flows);
    // If freeze has a selectedFlow and it exists, use it
    if (activeFlowKey && flows[activeFlowKey]) return flows[activeFlowKey];
    // If only one flow, auto-select
    if (keys.length === 1) return flows[keys[0]];
    // Releases/amendments don't ask the workflow question when using the default release filter
    const isFollowOn = isRelease || isAmendment;
    if (isFollowOn && keys.length > 0) return flows[keys[0]];
    return null;
  }, [flows, activeFlowKey, isRelease, isAmendment]);

  // Step types that apply to releases when no dedicated releaseFlows is defined
  const RELEASE_STEP_TYPES = new Set(['user-agreement', 'county-notice', 'biometrics', 'payment', 'summary']);

  // Steps from the active flow, filtered for releases unless dedicated releaseFlows is provided
  const steps = React.useMemo(() => {
    if (!activeFlow?.steps) return [];
    const allSteps = activeFlow.steps.map((s: any) => ({
      name: s.name,
      label: s.label || s.name,
      type: s.type || s.name,
      config: s.config,
      contentHtml: s.contentHtml,
      content: s.content,
      requireAcknowledgment: s.requireAcknowledgment,
      nextStep: s.nextStep || s.config?.nextStep,
      prevStep: s.prevStep || s.backStep || s.config?.prevStep || s.config?.backStep,
      hideNextStep: s.hideNextStep || s.config?.hideNextStep,
    }));
    if (isRelease && !hasDedicatedReleaseFlow) {
      return allSteps.filter((s: any) => RELEASE_STEP_TYPES.has(s.type));
    }

    return allSteps;
  }, [activeFlow, isRelease, hasDedicatedReleaseFlow]);

  const findStepByName = React.useCallback(
    (name?: string | null) => {
      if (!name) return undefined;
      const lower = String(name).toLowerCase();
      return steps.find((s: any) => String(s.name).toLowerCase() === lower);
    },
    [steps]
  );

  // Auto-redirect to summary step if document is already generated
  React.useEffect(() => {
    if (document && document.lookup === documentLookup && document.generated && steps.length > 0) {
      const summaryStep = steps.find((s: any) => s.type === 'summary');
      const targetName = summaryStep?.name || 'summary';
      if (step?.toLowerCase() !== targetName.toLowerCase()) {
        navigate(`/document/${documentLookup}/${targetName}`, { replace: true });
      }
    }
  }, [document, step, steps, documentLookup, navigate]);

  // Auto-start flow: go to the first step that applies to this document (releases/amendments skip data-entry steps).
  // Wait until the document is loaded so we know whether it is a lien, release or amendment.
  React.useEffect(() => {
    if (!documentLookup || !document || document.lookup !== documentLookup || steps.length === 0) return;
    const notInFlow = step !== 'flow-select' && !findStepByName(step);
    if (step === 'start' || ((isRelease || isAmendment) && notInFlow)) {
      navigate(`/document/${documentLookup}/${steps[0].name}`, { replace: true });
    }
  }, [step, steps, documentLookup, document, isRelease, isAmendment, navigate, findStepByName]);

  const stepIndex = steps.findIndex((s: any) => String(s.name).toLowerCase() === String(step || '').toLowerCase());
  const currentStepObj = stepIndex >= 0 ? steps[stepIndex] : null;

  const [agreed, setAgreed] = useState(false);
  const [saveError] = useState<string | null>(null);
  const [saving] = useState(false);

  // =========================================================================
  // Deterministic Breadcrumb History (sessionStorage + location.state)
  // =========================================================================
  const breadcrumbStorageKey = documentLookup ? `fzd_breadcrumbs_${documentLookup}` : null;

  const readStoredBreadcrumbs = React.useCallback((): Array<{ name: string; label: string }> => {
    if (breadcrumbStorageKey) {
      try {
        const raw = sessionStorage.getItem(breadcrumbStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch {}
    }
    const locState = location.state as any;
    return Array.isArray(locState?.breadcrumbs) ? locState.breadcrumbs : [];
  }, [breadcrumbStorageKey, location.state]);

  const writeStoredBreadcrumbs = React.useCallback((trail: Array<{ name: string; label: string }>) => {
    if (!breadcrumbStorageKey) return;
    try {
      sessionStorage.setItem(breadcrumbStorageKey, JSON.stringify(trail));
    } catch {}
  }, [breadcrumbStorageKey]);

  // Rewind stored breadcrumbs whenever the active step is already in the trail (e.g. browser Back button)
  const breadcrumbs = React.useMemo(() => {
    const trail = readStoredBreadcrumbs();
    if (!currentStepObj) return trail;
    const existingIdx = trail.findIndex(
      (b) => String(b.name).toLowerCase() === String(currentStepObj.name).toLowerCase()
    );
    if (existingIdx >= 0) {
      const truncated = trail.slice(0, existingIdx);
      writeStoredBreadcrumbs(truncated);
      return truncated;
    }
    return trail;
  }, [readStoredBreadcrumbs, writeStoredBreadcrumbs, currentStepObj]);

  // Next Step resolution (case-insensitive)
  const explicitNextName = currentStepObj?.nextStep;
  const resolvedNextStep = explicitNextName
    ? findStepByName(explicitNextName)
    : steps[stepIndex + 1];
  const nextStepLabel = resolvedNextStep?.label;

  // Previous Step resolution:
  // 1. Explicit prevStep / backStep in JSON if defined
  // 2. Breadcrumb trail (actual path the user took through options/branches)
  // 3. Branch-aware fallback walk backwards from stepIndex - 1
  const resolvedPrevStep = React.useMemo(() => {
    if (!currentStepObj || stepIndex <= 0) return undefined;

    let candidate: any = undefined;

    if (currentStepObj.prevStep) {
      candidate = findStepByName(currentStepObj.prevStep);
    }

    if (!candidate && breadcrumbs.length > 0) {
      for (let i = breadcrumbs.length - 1; i >= 0; i--) {
        const found = findStepByName(breadcrumbs[i].name);
        if (found && String(found.name).toLowerCase() !== String(currentStepObj.name).toLowerCase()) {
          candidate = found;
          break;
        }
      }
    }

    if (!candidate) {
      // Walk backwards to find the step that could logically lead into currentStepObj
      for (let i = stepIndex - 1; i >= 0; i--) {
        const prevCandidate = steps[i];
        if (prevCandidate.type === 'options' && Array.isArray(prevCandidate.config?.options)) {
          const canLeadHere = prevCandidate.config.options.some(
            (opt: any) => String(opt?.nextStep || '').toLowerCase() === String(currentStepObj.name).toLowerCase()
          );
          if (canLeadHere) {
            candidate = prevCandidate;
            break;
          }
        }
        // If prevCandidate has an explicit nextStep pointing somewhere else (e.g. a sibling branch), skip it
        if (
          prevCandidate.nextStep &&
          String(prevCandidate.nextStep).toLowerCase() !== String(currentStepObj.name).toLowerCase()
        ) {
          continue;
        }
        candidate = prevCandidate;
        break;
      }
    }

    // If the resolved previous step is a payment step that is already paid (which auto-forwards), step back one more
    if (candidate && candidate.type === 'payment' && document?.paid) {
      if (candidate.prevStep) {
        const beforePayment = findStepByName(candidate.prevStep);
        if (beforePayment) return beforePayment;
      }
      const paymentIdx = steps.findIndex((s: any) => s.name === candidate.name);
      if (paymentIdx > 0) return steps[paymentIdx - 1];
    }

    return candidate;
  }, [currentStepObj, stepIndex, breadcrumbs, steps, findStepByName, document?.paid]);

  const prevStepLabel = resolvedPrevStep?.label;

  // =========================================================================
  // Navigation helpers — always use unified route
  // =========================================================================
  const goToStep = (name: string) => {
    if (documentLookup) {
      const targetStep = findStepByName(name);
      const targetName = targetStep ? targetStep.name : name;
      const newBreadcrumbs = [...breadcrumbs];
      if (currentStepObj) {
        newBreadcrumbs.push({ name: currentStepObj.name, label: currentStepObj.label });
      }
      writeStoredBreadcrumbs(newBreadcrumbs);
      navigate(`/document/${documentLookup}/${targetName}`, {
        state: { breadcrumbs: newBreadcrumbs }
      });
    }
  };

  const goToNextStep = () => {
    if (resolvedNextStep) {
      goToStep(resolvedNextStep.name);
    }
  };

  const goToPrevStep = () => {
    if (!documentLookup || !resolvedPrevStep) return;
    const targetIdx = breadcrumbs.findIndex(
      (b) => String(b.name).toLowerCase() === String(resolvedPrevStep.name).toLowerCase()
    );
    const newBreadcrumbs = targetIdx >= 0 ? breadcrumbs.slice(0, targetIdx) : breadcrumbs.slice(0, -1);
    writeStoredBreadcrumbs(newBreadcrumbs);
    navigate(`/document/${documentLookup}/${resolvedPrevStep.name}`, {
      state: { breadcrumbs: newBreadcrumbs }
    });
  };

  // =========================================================================
  // Flow selection handler — persists selectedFlow to freeze
  // =========================================================================
  const handleSelectFlow = async (flowKey: string) => {
    setLocalSelectedFlow(flowKey);
    // Persist to freeze record
    if (freeze?.lookup && apiBaseUrl) {
      try {
        const token = getToken ? await getToken() : null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/select-flow`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ selectedFlow: flowKey }),
        });
        // Update local freeze state
        setFreeze({ ...freeze, selectedFlow: flowKey } as any);
      } catch (err) {
        console.error('Failed to persist selectedFlow:', err);
      }
    }
    // Navigate to the first step of the selected flow
    if (flows && flows[flowKey]) {
      const selectedSteps = flows[flowKey].steps;
      if (selectedSteps?.length > 0 && documentLookup) {
        navigate(`/document/${documentLookup}/${selectedSteps[0].name}`, { replace: true });
      }
    }
  };

  // =========================================================================
  // Handlers (shared across all step types)
  // =========================================================================
  const [creatingLien, setCreatingLien] = useState(false);
  const [paying, setPaying] = useState(false);
  const handlePayNow = async () => {
    setPaying(true);
    setPaymentModalOpen(true);
    setPaying(false);
  };
  const handleGenerateDocument = () => {
    setConfirmModalOpen(true);
    setConfirmModalLoading(false);
    setConfirmModalSuccess(false);
  };
  const handleConfirmGenerate = async () => {
    setConfirmModalLoading(true);
    setConfirmModalSuccess(false);
    if (!documentLookup || !apiBaseUrl) {
      window.alert('Missing document lookup or API base URL.');
      setConfirmModalLoading(false);
      return;
    }
    try {
      const token = getToken ? await getToken() : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/generate`, {
        method: 'POST',
        headers,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || 'Failed to generate document');
      }
      try {
        const updatedRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}`, { headers });
        if (updatedRes.ok) {
          const updatedData = await updatedRes.json();
          if (updatedData?.data) setDocument(updatedData.data);
        }
      } catch (fetchErr) {
        console.error('Failed to refetch document after generation:', fetchErr);
      }
      setConfirmModalLoading(false);
      setConfirmModalSuccess(true);
      setTimeout(() => {
        setConfirmModalOpen(false);
        setConfirmModalSuccess(false);
        navigate(`/document/${documentLookup}/details`, { replace: true });
      }, 2000);
    } catch (err) {
      setConfirmModalLoading(false);
      const errMsg = err instanceof Error ? err.message : String(err);
      setOverlayError(errMsg);
      window.alert('Failed to generate document: ' + errMsg);
    }
  };
  const handleCancelGenerate = () => {
    setConfirmModalOpen(false);
    setConfirmModalLoading(false);
    setConfirmModalSuccess(false);
  };
  const handleTakeBiometrics = (entityId: string) => {
    console.log('Take biometrics for entity', entityId);
  };

  // Download Document handler
  const handleDownloadDocument = async () => {
    if (!documentLookup || !apiBaseUrl) {
      console.error('Missing document lookup or apiBaseUrl');
      return;
    }
    try {
      const token = getToken ? await getToken() : null;
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/generated`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) throw new Error('Failed to download document');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `document-${documentLookup}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
    } catch (err) {
      console.error('Error downloading document:', err);
      window.alert('Error downloading document: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  // =========================================================================
  // Render guards
  // =========================================================================
  if (freezeError) {
    return (
      <div className="flex flex-col items-center w-full bg-white mt-12">
        <div className="text-red-600 text-lg font-bold mb-4">Freeze not found.</div>
        <Link to="/search" className="text-blue-600 underline">Go to Search Page</Link>
      </div>
    );
  }
  if (loadingFreeze) {
    return <div className="flex flex-col items-center w-full bg-white mt-12">Loading freeze...</div>;
  }
  if (apiBaseUrlLoading) return <div>Loading configuration...</div>;
  if (apiBaseUrlError) return <div>Error loading configuration: {apiBaseUrlError}</div>;

  const stepType = currentStepObj?.type || step;
  const canGoBack = Boolean(resolvedPrevStep);

  // Payment step auto-open / auto-skip effect (avoids re-opening modal in a render loop if user closes it)
  React.useEffect(() => {
    if (stepType !== 'payment') return;
    if (document?.paid) {
      goToNextStep();
    } else if (document && !paying) {
      setPaymentModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepType, currentStepObj?.name, document?.paid]);

  // =========================================================================
  // Flow selector: if multiple flows and none selected yet
  // =========================================================================
  if (step === 'flow-select' || (step === 'start' && !activeFlow && flows && Object.keys(flows).length > 1) || (!activeFlow && flows && Object.keys(flows).length > 1)) {
    const flowOptions = Object.entries(flows!).map(([key, flow]) => ({
      key,
      label: flow.label,
      description: flow.description,
    }));
    return (
      <>
        <OverlaySpinner open={loadingOverlay} message={overlayError ? overlayError : "Loading..."} />
        <div className="flex flex-col items-center w-full bg-white">
          <div className="w-full max-w-[700px] mt-8 mb-8">
            <div className="w-full text-center text-[28px] font-plus-jakarta font-bold text-[#141435] mt-8">
              Select Workflow
            </div>
          </div>
          <div className="bg-white rounded-2xl px-10 py-8 flex flex-col items-center max-w-[700px] w-full">
            <FlowSelectorCard flows={flowOptions} onSelectFlow={handleSelectFlow} />
          </div>
        </div>
      </>
    );
  }

  // No flows defined at all
  if (!flows || !activeFlow || steps.length === 0) {
    if (!loadingOverlay && !loadingFreeze) {
      return (
        <div className="flex flex-col items-center justify-center w-full bg-white mt-20 p-10 text-center max-w-xl mx-auto border border-gray-200 rounded-2xl shadow-sm">
          <h2 className="text-[#141435] text-[22px] font-plus-jakarta font-bold mb-3">
            Error Loading County Template
          </h2>
          <p className="text-[#485D7D] text-[15px] leading-relaxed mb-4">
            We are unable to load the filing template for this county at this time. Please return to the search page or contact support for assistance.
          </p>
          <span className="inline-block text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1 rounded-full mb-6">
            Error #FZD-101: County Template Missing
          </span>
          <Link
            to="/search"
            className="bg-[#0B4BB6] text-white font-plus-jakarta font-semibold text-sm px-6 py-2.5 rounded-full hover:bg-[#23235a] transition-colors"
          >
            Return to Search
          </Link>
        </div>
      );
    }
    return <OverlaySpinner open={true} message="Loading workflow..." />;
  }

  // =========================================================================
  // Step content rendering — unified switch for all step types
  // =========================================================================
  let stepContent;
  switch (stepType) {
    case 'county-notice': {
      let noticeList: CountyNotice[] = [];
      if (currentStepObj?.contentHtml || currentStepObj?.content) {
        // Inline notice from step config
        noticeList = [{
          title: currentStepObj.label || 'County Notice',
          contentHtml: currentStepObj.contentHtml || currentStepObj.content || '',
          requireAcknowledgment: currentStepObj.requireAcknowledgment !== false,
          triggerStep: 'beforeDocumentCreation'
        }];
      } else {
        // Look for notices in the schema
        const schemaNotices = schema?.notices || schema?.uiFlows?.notices || [];
        noticeList = schemaNotices.filter((n: any) => n.triggerStep === 'beforeDocumentCreation' || !n.triggerStep);
        if (noticeList.length === 0) {
          noticeList = [{ title: currentStepObj?.label || 'County Notice', contentHtml: '<p>No notice configured for this county.</p>', requireAcknowledgment: currentStepObj?.requireAcknowledgment !== false, triggerStep: 'beforeDocumentCreation' }];
        }
      }
      stepContent = (
        <CountyNoticeCard
          notices={noticeList}
          onBack={canGoBack ? goToPrevStep : undefined}
          onContinue={goToNextStep}
        />
      );
      break;
    }
    case 'user-agreement':
      stepContent = (
        <UserAgreementCard
          agreed={agreed}
          setAgreed={setAgreed}
          onContinue={goToNextStep}
        />
      );
      break;
    case 'property-info':
      stepContent = (
        <>
          {freeze && (
            <PropertyInfoCard
              property={freeze as any}
              setProperty={setFreeze as any}
              onContinue={goToNextStep}
              documentType={document?.type}
            />
          )}
          {saveError && <div className="text-red-500 mt-2">{saveError}</div>}
          {saving && <div className="text-blue-500 mt-2">Saving...</div>}
        </>
      );
      break;
    case 'document-details':
      stepContent = (
        <DocumentDetailsCard onContinue={goToNextStep} />
      );
      break;
    case 'instrument':
    case 'freeze-indexes':
      stepContent = (
        <InstrumentDetailsCard
          property={freeze as Freeze}
          setProperty={setFreeze as any}
          onContinue={goToNextStep}
          stepConfig={currentStepObj?.config}
        />
      );
      break;
    case 'simple-instrument':
      if (currentStepObj?.config?.fields?.length > 0) {
        stepContent = (
          <DynamicFormCard
            key={documentLookup}
            config={currentStepObj?.config}
            initialData={(document?.customData as any) || {}}
            onBack={canGoBack ? goToPrevStep : undefined}
            onContinue={async (data) => {
              try {
                const token = getToken ? await getToken() : null;
                await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/custom-data`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
                  body: JSON.stringify({ customData: data })
                });
                if (document) {
                  setDocument({ ...document, customData: { ...(document.customData || {}), ...data } } as any);
                }
              } catch (e) {
                console.error('Error saving step data:', e);
              }
              goToNextStep();
            }}
          />
        );
      } else {
        stepContent = (
          <SimpleInstrumentCard
            property={freeze as Freeze}
            setProperty={setFreeze as any}
            onContinue={goToNextStep}
          />
        );
      }
      break;
    case 'portfolio-builder':
      stepContent = (
        <PortfolioBuilderCard
          key={`${documentLookup}-${currentStepObj?.name || 'portfolio'}`}
          config={currentStepObj?.config}
          document={document}
          setDocument={setDocument}
          onContinue={goToNextStep}
          onBack={canGoBack ? goToPrevStep : undefined}
        />
      );
      break;
    case 'parcels':
    case 'parcel':
      stepContent = (
        <ParcelCollectionCard
          key={documentLookup}
          config={currentStepObj?.config}
          freezeLookup={freeze?.lookup}
          onBack={canGoBack ? goToPrevStep : undefined}
          onContinue={goToNextStep}
        />
      );
      break;
    case 'form':
      stepContent = (
        <DynamicFormCard
          key={documentLookup}
          config={currentStepObj?.config}
          onBack={canGoBack ? goToPrevStep : undefined}
          onContinue={async (formData: Record<string, any>) => {
            try {
              const token = getToken ? await getToken() : null;
              const headers: Record<string, string> = { 'Content-Type': 'application/json' };
              if (token) headers['Authorization'] = `Bearer ${token}`;
              if (documentLookup) {
                await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/custom-data`, {
                  method: 'PUT',
                  headers,
                  body: JSON.stringify({ customData: formData }),
                });
                setDocument({ ...document, customData: { ...(document?.customData || {}), ...formData } } as any);
              }
            } catch (err) {
              console.error('Error saving form data:', err);
            }
            goToNextStep();
          }}
        />
      );
      break;
    case 'custom-fields':
      stepContent = (
        <CustomFieldsCard
          config={currentStepObj?.config}
          onBack={canGoBack ? goToPrevStep : undefined}
          onContinue={goToNextStep}
        />
      );
      break;
    case 'property-address':
      stepContent = (
        <SimplePropertyAddressCard
          key={documentLookup}
          onContinue={goToNextStep}
          documentType={document?.type}
        />
      );
      break;
    case 'options':
      stepContent = (
        <OptionsCard
          config={currentStepObj?.config}
          onSelect={(nextStep) => goToStep(nextStep)}
          onBack={canGoBack ? goToPrevStep : undefined}
        />
      );
      break;
    case 'grantors':
      stepContent = (
        <AdvancedGrantorCard
          config={{
            showMaritalStatus: schema?.showMaritalStatus,
            allowNonParticipating: schema?.grantorConfig?.allowNonParticipants || schema?.grantorConfig?.allowNonParticipating,
            ...(schema?.grantorConfig || {}),
            ...(currentStepObj?.config || {}),
          }}
          onBack={canGoBack ? goToPrevStep : undefined}
          onContinue={goToNextStep}
          entityId={documentLookup}
          documentType={document?.type}
          readOnly={isRelease}
        />
      );
      break;
    case 'biometrics':
      stepContent = (
        <BiometricsCard
          onBack={canGoBack ? goToPrevStep : undefined}
          onTakeBiometrics={handleTakeBiometrics}
          onSaveDraft={() => {}}
          onSubmit={stepIndex < steps.length - 1 ? goToNextStep : handleGenerateDocument}
          creatingLien={creatingLien}
        />
      );
      break;
    case 'payment':
      stepContent = (
        <div className="w-full flex flex-col items-center gap-6">
          <div className="w-full bg-[#F8FAFC] border border-[#D7DFEC] rounded-xl p-6 text-center">
            <h3 className="text-[18px] font-plus-jakarta font-bold text-[#141435] mb-2">
              Filing Payment
            </h3>
            <p className="text-[#485D7D] text-sm font-plus-jakarta">
              Please complete payment to proceed with identity verification and document generation.
            </p>
          </div>
          <div className="flex flex-row w-full gap-4">
            {canGoBack && (
              <button
                type="button"
                className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
                onClick={goToPrevStep}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="flex-1 bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#23235a] transition-colors"
              onClick={() => setPaymentModalOpen(true)}
            >
              Proceed to Payment
            </button>
          </div>
          <PaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            documentId={documentLookup || ''}
            amount={county?.defaultChargeAmount || 5000}
            onSuccess={async (paymentIntentId) => {
              if (!documentLookup) return;
              const token = getToken ? await getToken() : null;
              const headers: Record<string, string> = { 'Content-Type': 'application/json' };
              if (token) headers['Authorization'] = `Bearer ${token}`;
              try {
                await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/payment`, {
                  method: 'PUT',
                  headers,
                  body: JSON.stringify({ paymentIntentId }),
                });
                const docRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}`, { headers });
                if (docRes.ok) {
                  const docData = await docRes.json();
                  if (docData?.data) {
                    setDocument(docData.data);
                  } else if (document) {
                    setDocument({ ...document, paid: true });
                  }
                } else if (document) {
                  setDocument({ ...document, paid: true });
                }
              } catch (e) {
                console.error("Failed to update payment status on backend", e);
                if (document) {
                  setDocument({ ...document, paid: true });
                }
              }
              setPaymentModalOpen(false);
              goToNextStep();
            }}
          />
        </div>
      );
      break;
    case 'summary':
      stepContent = (
        <>
          <SummaryCard
            config={{
              instrument: { fields: steps.flatMap((s: any) => s.config?.instrument?.fields || []) },
              parcel: { fields: steps.flatMap((s: any) => s.config?.parcel?.fields || []) }
            }}
            nextStepLabel={stepIndex < steps.length - 1 ? "Continue" : "Generate Document"}
            onPayNow={handlePayNow}
            onBack={canGoBack ? goToPrevStep : undefined}
            onGenerateDocument={stepIndex < steps.length - 1 ? goToNextStep : handleGenerateDocument}
            generating={creatingLien}
            paying={paying}
            onDownloadDocument={handleDownloadDocument}
            generated={document?.generated}
          />
          <PaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            documentId={documentLookup || ''}
            amount={county?.defaultChargeAmount || 5000}
            onSuccess={async (paymentIntentId) => {
              if (!documentLookup) return;
              const token = getToken ? await getToken() : null;
              const headers: Record<string, string> = { 'Content-Type': 'application/json' };
              if (token) headers['Authorization'] = `Bearer ${token}`;
              
              try {
                await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}/payment`, {
                  method: 'PUT',
                  headers,
                  body: JSON.stringify({ paymentIntentId }),
                });
                const docRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentLookup}`, { headers });
                if (docRes.ok) {
                  const docData = await docRes.json();
                  if (docData?.data) {
                    setDocument(docData.data);
                  } else if (document) {
                    setDocument({ ...document, paid: true });
                  }
                }
              } catch (err) {
                console.error("Payment sync failed:", err);
              }
              setPaymentModalOpen(false);
              goToNextStep();
            }}
          />
        </>
      );
      break;
    case 'last-chance':
      stepContent = (
        <LastChanceReviewCard
          onBack={canGoBack ? goToPrevStep : undefined}
          onGenerateDocument={handleGenerateDocument}
          generating={creatingLien}
        />
      );
      break;
    default:
      stepContent = (
        <div className="w-full mb-6 text-center p-6 border border-gray-200 rounded-xl bg-gray-50">
          <div className="text-lg font-plus-jakarta font-bold text-[#141435] mb-2">
            Error Loading Workflow Step
          </div>
          <p className="text-sm text-[#485D7D] mb-3">
            This workflow step could not be loaded.
          </p>
          <span className="inline-block text-xs font-mono text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full">
            Error #FZD-103: Unrecognized Step ({String(stepType)})
          </span>
        </div>
      );
  }

  return (
    <>
      <OverlaySpinner open={loadingOverlay} message={overlayError ? overlayError : "Loading document and property info..."} />
      <div className="flex flex-col items-center w-full bg-white">
        {/* Stepper */}
        <div className="w-full max-w-[700px] mt-8 mb-8">
          <Stepper 
            currentStepLabel={currentStepObj?.label || "Unknown Step"}
            prevStepLabel={prevStepLabel}
            nextStepLabel={nextStepLabel}
            onPrev={goToPrevStep}
            hideNextStep={currentStepObj?.hideNextStep || currentStepObj?.config?.hideNextStep}
          />
        </div>
        {/* Card for step content */}
        <div className="bg-white rounded-2xl px-10 py-8 flex flex-col items-center max-w-[700px] w-full">
          {stepContent}
        </div>
      </div>
      <ConfirmGenerateModal
        open={confirmModalOpen}
        onCancel={handleCancelGenerate}
        onConfirm={handleConfirmGenerate}
        loading={confirmModalLoading}
        success={confirmModalSuccess}
      />
    </>
  );
};

const FlowPage: React.FC = () => <FlowPageInner />;

export default FlowPage;
